import { createRouter, createWebHistory } from 'vue-router'
import LoginPage from '@/pages/LoginPage.vue'
import DashboardPage from '@/pages/DashboardPage.vue'
import OrganisationListPage from '@/pages/organisations/OrganisationListPage.vue'
import OrganisationDetailPage from '@/pages/organisations/OrganisationDetailPage.vue'
import UserListPage from '@/pages/users/UserListPage.vue'
import UserDetailPage from '@/pages/users/UserDetailPage.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'

const ADMIN_META = { requiresAdmin: true } as const

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'admin.login',
      component: LoginPage,
    },
    {
      path: '/',
      name: 'admin.dashboard',
      component: DashboardPage,
      meta: ADMIN_META,
    },
    {
      path: '/organisations',
      name: 'admin.organisations.index',
      component: OrganisationListPage,
      meta: ADMIN_META,
    },
    {
      path: '/organisations/:id',
      name: 'admin.organisations.show',
      component: OrganisationDetailPage,
      meta: ADMIN_META,
    },
    {
      path: '/users',
      name: 'admin.users.index',
      component: UserListPage,
      meta: ADMIN_META,
    },
    {
      path: '/users/:id',
      name: 'admin.users.show',
      component: UserDetailPage,
      meta: ADMIN_META,
    },
    {
      path: '/support',
      name: 'admin.support.index',
      component: () => import('@/pages/support/AdminSupportListPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/support/:id',
      name: 'admin.support.show',
      component: () => import('@/pages/support/AdminSupportDetailPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/settings',
      name: 'admin.settings.index',
      component: () => import('@/pages/settings/PlatformSettingsPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/pricing',
      name: 'admin.pricing.index',
      component: () => import('@/pages/settings/PlatformPricingPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/settings/history',
      name: 'admin.settings.history',
      component: () => import('@/pages/settings/SettingsHistoryPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/system/sessions',
      name: 'admin.system.sessions.index',
      component: () => import('@/pages/system/SessionListPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/system/scheduled-tasks',
      name: 'admin.system.scheduled-tasks.index',
      component: () => import('@/pages/system/ScheduledTaskListPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/system/scheduled-tasks/:command',
      name: 'admin.system.scheduled-tasks.show',
      component: () => import('@/pages/system/ScheduledTaskDetailPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/system/analytics',
      name: 'admin.system.analytics.index',
      component: () => import('@/pages/system/AnalyticsPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/finance/transactions',
      name: 'admin.finance.transactions.index',
      component: () => import('@/pages/finance/FinanceTransactionListPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/invoicing',
      name: 'admin.invoicing.index',
      component: () => import('@/pages/invoicing/InvoicingPage.vue'),
      meta: ADMIN_META,
    },
    {
      path: '/invoicing/stripe-fee-refunds',
      name: 'admin.invoicing.stripe-fee-refunds',
      component: () => import('@/pages/invoicing/InvoicingStripeFeeRefundsPage.vue'),
      meta: ADMIN_META,
    },
    // Catch-all redirect to login
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'admin.login' },
    },
  ],
})

router.beforeEach(async (to) => {
  if (!to.meta.requiresAdmin) {
    return
  }

  const { isAuthenticated, isLoading, fetchCurrentSuperAdmin } = useAdminAuth()

  // If we already know the user is authenticated, allow navigation.
  if (isAuthenticated.value) {
    return
  }

  // If auth check is in progress, wait for it.
  if (isLoading.value) {
    await new Promise<void>((resolve) => {
      const stop = setInterval(() => {
        if (!isLoading.value) {
          clearInterval(stop)
          resolve()
        }
      }, 50)
    })
    if (isAuthenticated.value) {
      return
    }
  }

  // Attempt to validate the stored token by fetching the current user.
  const token = localStorage.getItem('startline_admin_token')
  if (token) {
    await fetchCurrentSuperAdmin()
    if (isAuthenticated.value) {
      return
    }
  }

  return { name: 'admin.login' }
})

export default router
